import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ClaimMetadata } from "@/types/claims";
import {
    METADATA_FIELDS,
    FIELDS_BY_CATEGORY,
    getValue,
    formatValue,
    validateField,
    type MetadataField,
} from "./metadata-fields";

// --- Types ---

type FieldSource = "insurance" | "contractor" | "manual";

// --- Sub-components ---

interface MetadataFieldRowProps {
    field: MetadataField;
    insuranceValue: any;
    contractorValue: any;
    currentSelection: FieldSource;
    manualValue: string;
    validationError: string | null;
    onSelectionChange: (key: string, value: FieldSource) => void;
    onManualChange: (key: string, value: string) => void;
}

function MetadataFieldRow({
    field,
    insuranceValue,
    contractorValue,
    currentSelection,
    manualValue,
    validationError,
    onSelectionChange,
    onManualChange
}: MetadataFieldRowProps) {
    const hasConflict = insuranceValue && contractorValue && insuranceValue !== contractorValue;

    return (
        <div className="pb-2">
            <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-medium text-muted-foreground">{field.label}</Label>
                <div className="flex items-center gap-2">
                    {validationError && (
                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/20">
                            <AlertCircle className="h-2.5 w-2.5 text-red-500" />
                            <span className="text-[10px] font-medium text-red-600 dark:text-red-400">{validationError}</span>
                        </div>
                    )}
                    {hasConflict && (
                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/10">
                            <AlertCircle className="h-2.5 w-2.5 text-amber-500" />
                            <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">Conflict</span>
                        </div>
                    )}
                </div>
            </div>

            <RadioGroup
                value={currentSelection}
                onValueChange={(value) => onSelectionChange(field.key, value as FieldSource)}
                className="grid grid-cols-3 gap-2"
            >
                {/* Insurance Option */}
                <div className={cn(
                    "relative flex items-start space-x-2 rounded-md border p-2 transition-all cursor-pointer",
                    currentSelection === "insurance" ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" :
                        insuranceValue ? "border-border hover:bg-muted/50" : "border-border/50 opacity-50 cursor-not-allowed",
                    validationError && currentSelection === "insurance" && "border-red-500 bg-red-50/10"
                )}>
                    <RadioGroupItem value="insurance" id={`${field.key}-insurance`} className="mt-0.5" disabled={!insuranceValue} />
                    <Label htmlFor={`${field.key}-insurance`} className="flex-1 cursor-pointer text-xs space-y-0.5">
                        <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3 text-blue-500" />
                            <span className="font-medium text-blue-600 dark:text-blue-400">Insurance</span>
                        </div>
                        <div className="text-foreground font-medium break-words">{formatValue(insuranceValue, field.enumOptions)}</div>
                    </Label>
                </div>

                {/* Contractor Option */}
                <div className={cn(
                    "relative flex items-start space-x-2 rounded-md border p-2 transition-all cursor-pointer",
                    currentSelection === "contractor" ? "border-green-500 bg-green-50/50 dark:bg-green-950/20" :
                        contractorValue ? "border-border hover:bg-muted/50" : "border-border/50 opacity-50 cursor-not-allowed",
                    validationError && currentSelection === "contractor" && "border-red-500 bg-red-50/10"
                )}>
                    <RadioGroupItem value="contractor" id={`${field.key}-contractor`} className="mt-0.5" disabled={!contractorValue} />
                    <Label htmlFor={`${field.key}-contractor`} className="flex-1 cursor-pointer text-xs space-y-0.5">
                        <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3 text-green-500" />
                            <span className="font-medium text-green-600 dark:text-green-400">Contractor</span>
                        </div>
                        <div className="text-foreground font-medium break-words">{formatValue(contractorValue, field.enumOptions)}</div>
                    </Label>
                </div>

                {/* Manual Option */}
                <div className={cn(
                    "relative flex items-start space-x-2 rounded-md border p-2 transition-all cursor-pointer",
                    currentSelection === "manual" ? "border-purple-500 bg-purple-50/50 dark:bg-purple-950/20" : "border-border hover:bg-muted/50",
                    validationError && currentSelection === "manual" && "border-red-500"
                )}>
                    <RadioGroupItem value="manual" id={`${field.key}-manual`} className="mt-0.5" />
                    <Label htmlFor={`${field.key}-manual`} className="flex-1 cursor-pointer text-xs space-y-0.5">
                        <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3 text-purple-500" />
                            <span className="font-medium text-purple-600 dark:text-purple-400">Manual Entry</span>
                        </div>
                        <div className="w-full">
                            {field.enumOptions ? (
                                <select
                                    value={manualValue}
                                    onChange={(e) => onManualChange(field.key, e.target.value)}
                                    className={cn(
                                        "w-full mt-1 px-2 py-1 text-xs rounded border bg-background",
                                        validationError && currentSelection === "manual" ? "border-red-500 focus:ring-red-500" : "border-input focus:ring-purple-500"
                                    )}
                                >
                                    <option value="">Select...</option>
                                    {field.enumOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={manualValue}
                                    onChange={(e) => onManualChange(field.key, e.target.value)}
                                    placeholder="Enter value..."
                                    className={cn(
                                        "w-full mt-1 px-2 py-1 text-xs rounded border bg-background",
                                        validationError && currentSelection === "manual" ? "border-red-500 focus:ring-red-500" : "border-input focus:ring-purple-500"
                                    )}
                                />
                            )}
                            {validationError && currentSelection === "manual" && (
                                <p className="text-[10px] text-red-500 mt-0.5">{validationError}</p>
                            )}
                        </div>
                    </Label>
                </div>
            </RadioGroup>
        </div>
    );
}

// --- Main Component ---

interface MetadataReviewModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    insuranceMetadata: ClaimMetadata | null;
    contractorMetadata: ClaimMetadata | null;
    onConfirm: (selectedMetadata: ClaimMetadata) => void;
    isProcessing?: boolean;
}

export default function MetadataReviewModal({
    open,
    onOpenChange,
    insuranceMetadata,
    contractorMetadata,
    onConfirm,
    isProcessing
}: MetadataReviewModalProps) {
    const [selections, setSelections] = useState<{ [key: string]: FieldSource }>({});
    const [manualValues, setManualValues] = useState<{ [key: string]: string }>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});
    const userExplicitSelectionsRef = useRef<Set<string>>(new Set());

    // Effect: Initialize selections
    useEffect(() => {
        if (!open) return;

        // Helper to calculate default selection
        const calculateDefault = (key: string): FieldSource => {
            // Insurance Company should never default to contractor
            const cVal = key === "estimatorCompany" ? null : getValue(contractorMetadata, key);
            const iVal = getValue(insuranceMetadata, key);
            return cVal ? "contractor" : iVal ? "insurance" : "manual";
        };

        if (userExplicitSelectionsRef.current.size === 0) {
            // Initial load
            const initial: { [key: string]: FieldSource } = {};
            METADATA_FIELDS.forEach(f => initial[f.key] = calculateDefault(f.key));
            setSelections(initial);
        } else {
            // Update on metadata change (respecting user overrides)
            setSelections(prev => {
                const next = { ...prev };
                METADATA_FIELDS.forEach(f => {
                    if (!userExplicitSelectionsRef.current.has(f.key)) {
                        const newDefault = calculateDefault(f.key);
                        if (!prev[f.key] || (prev[f.key] === "manual" && newDefault !== "manual")) {
                            next[f.key] = newDefault;
                        }
                    }
                });
                return next;
            });
        }
    }, [open, insuranceMetadata, contractorMetadata]);

    const handleSelectionChange = (key: string, value: FieldSource) => {
        setSelections(prev => ({ ...prev, [key]: value }));
        userExplicitSelectionsRef.current.add(key);
        if (validationErrors[key]) {
            setValidationErrors(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const handleManualChange = (key: string, value: string) => {
        setManualValues(prev => ({ ...prev, [key]: value }));
        if (selections[key] !== "manual") {
            handleSelectionChange(key, "manual");
        } else if (validationErrors[key]) {
            setValidationErrors(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const handleConfirm = () => {
        const errors: Record<string, string> = {};
        const combinedMetadata: any = {};

        METADATA_FIELDS.forEach(field => {
            const insuranceValue = getValue(insuranceMetadata, field.key);
            const contractorValue = getValue(contractorMetadata, field.key);

            // Determine active selection
            let selection = selections[field.key];
            if (!selection) {
                // Fallback if state missing
                selection = contractorValue ? "contractor" : insuranceValue ? "insurance" : "manual";
            }

            // Validate
            if (selection === "manual") {
                const value = manualValues[field.key] || "";
                const error = validateField(field.key, value);
                if (error) errors[field.key] = error;
            } else {
                const selectedValue = selection === "insurance" ? insuranceValue : contractorValue;
                const error = validateField(field.key, String(selectedValue || ""));
                if (error) errors[field.key] = error;
            }

            // Build Result
            let finalValue = null;
            if (selection === "manual") finalValue = manualValues[field.key]?.trim();
            else if (selection === "insurance") finalValue = insuranceValue;
            else if (selection === "contractor") finalValue = contractorValue;

            if (finalValue) {
                combinedMetadata[field.key] = finalValue;
            }
        });

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        onConfirm(combinedMetadata as ClaimMetadata);
    };

    const conflictCount = METADATA_FIELDS.filter(f => {
        const iVal = getValue(insuranceMetadata, f.key);
        const cVal = getValue(contractorMetadata, f.key);
        return iVal && cVal && iVal !== cVal;
    }).length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90dvh] md:max-h-[90vh] p-0 overflow-hidden flex flex-col">
                <DialogHeader className="px-6 py-5 border-b bg-muted/30 flex-shrink-0">
                    <div className="flex items-start justify-between">
                        <div>
                            <DialogTitle className="text-xl font-semibold">Review Extracted Metadata</DialogTitle>
                            <DialogDescription className="mt-1.5">Select which values to use for each field</DialogDescription>
                        </div>
                        {conflictCount > 0 && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                                <AlertCircle className="h-4 w-4 text-amber-500" />
                                <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                                    {conflictCount} conflicts detected
                                </span>
                            </div>
                        )}
                    </div>
                </DialogHeader>

                <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
                    <div className="px-6 py-4 space-y-4">
                        {Object.entries(FIELDS_BY_CATEGORY).map(([category, fields]) => (
                            <div key={category} className="space-y-2">
                                <h3 className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground px-1">{category}</h3>
                                <div className="space-y-2 rounded-lg border bg-card/50 p-3">
                                    {fields.map(field => {
                                        // Insurance Company should never show contractor value
                                        const contractorValue = field.key === "estimatorCompany"
                                            ? null
                                            : getValue(contractorMetadata, field.key);
                                        return (
                                            <div key={field.key} className={cn(!fields.indexOf(field) ? "" : "border-t pt-2")}>
                                                <MetadataFieldRow
                                                    field={field}
                                                    insuranceValue={getValue(insuranceMetadata, field.key)}
                                                    contractorValue={contractorValue}
                                                    currentSelection={selections[field.key] || (contractorValue ? "contractor" : getValue(insuranceMetadata, field.key) ? "insurance" : "manual")}
                                                    manualValue={manualValues[field.key] || ""}
                                                    validationError={validationErrors[field.key] || null}
                                                    onSelectionChange={handleSelectionChange}
                                                    onManualChange={handleManualChange}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>

                <DialogFooter className="px-6 py-4 border-t bg-muted/30 flex-shrink-0">
                    <div className="flex items-center justify-between w-full">
                        <div className="text-sm text-muted-foreground">Review all fields before creating</div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>Cancel</Button>
                            <Button onClick={handleConfirm} disabled={isProcessing} className="min-w-[140px]" data-testid="button-start-claim">
                                {isProcessing ? "Creating..." : "Create Claim"}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
